import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../lib/apiClient';

export type EmployeeRole = 'OWNER' | 'ADMIN' | 'JOB_MANAGER' | 'ESTIMATOR' | 'FIELD_LEAD' | 'JOB_LEAD';

export interface AuthUser {
  username: string;
  name: string;
  role: EmployeeRole;
  mustChangePassword: boolean;
  canManageUsers: boolean;
}

type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

interface MeResponse {
  ok: boolean;
  user?: AuthUser;
}

interface LoginResponse {
  ok: boolean;
  user?: AuthUser;
  message?: string;
  locked?: boolean;
  remainingAttempts?: number;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  const checkSession = useCallback(async () => {
    try {
      const result = await apiRequest<MeResponse>('/auth/me');
      if (result.ok && result.user) {
        setUser(result.user);
        setStatus('authenticated');
      } else {
        setUser(null);
        setStatus('unauthenticated');
      }
    } catch {
      // A network/API failure at startup should present as "please sign in"
      // rather than an unhandled error screen — the login form is always a
      // safe recovery state.
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  const login = useCallback(async (username: string, password: string) => {
    setError(null);
    try {
      const result = await apiRequest<LoginResponse>('/auth/login', {
        method: 'POST',
        body: { username, password },
      });
      if (!result.ok || !result.user) {
        setError(result.message || 'Invalid username or password.');
        return;
      }
      setUser(result.user);
      setStatus('authenticated');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unable to reach Portable Services right now.');
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiRequest('/auth/logout', { method: 'POST' });
    } finally {
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, error, login, logout }),
    [status, user, error, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
